import React, { useState, useEffect, useRef, useContext } from 'react'
import { useDispatch } from 'react-redux'

import { showReduxModal } from 'Reducers/UI/actions'
import { SnackBarContext } from 'Components/SnackBar'

import CampaignPage from '../../index'
import ConversionsView from '../component/ConversionsView'
import ConversionList from './ConversionList'
import ConversionStatistics from './ConversionStatistics'
import SelectReportType from '../../../../../Components/Campaigns/Page/Conversions/ModalBody/SelectReportType'
import ChangeReportType from '../../../../../Components/Campaigns/Page/Conversions/ModalBody/ChangeReportType'
import DeleteConversions from '../../../../../Components/Campaigns/Page/Conversions/ModalBody/DeleteConversions'
import NotAllowed from '../../../../../Components/Campaigns/Page/Conversions/ModalBody/NotAllowed'

import { CONVERSION_REPORT_TYPE } from '../../../../../shared/constants'
import { Dropzone } from '../../../../../Components/Form'

import useConversionsUpload from '../hook/useConversionsUpload'
import {
  getConversionReportType,
  updateConversionReportType,
  deleteConversions,
} from '../gql'
import useGraphQLFetch from '../../../../../Utils/Hooks/useGraphQLFetch'
import { validateRule } from '../../../../../Utils/Conversion'
import { StyledDeleteConversionsButton } from '../styles'

const Conversions = ({ match, nav, history }) => {
  const { campaignId } = match.params
  const [reportFile, setReportFile] = useState(null)
  const [reportType, setReportType] = useState(CONVERSION_REPORT_TYPE.NO_TYPE)
  const [campaignName, setCampaignName] = useState('')
  const [countryCode, setCountryCode] = useState('')
  const [metrics, setMetrics] = useState([])
  const [campaignType, setCampaignType] = useState(null)
  const [conversionsCount, setConversionsCount] = useState(0)
  const [paidConversionsCount, setPaidConversionsCount] = useState(0)
  const [primaryMetric, setPrimaryMetric] = useState('')
  const [otherMetrics, setOtherMetrics] = useState([])
  const dispatch = useDispatch()
  const dropzoneRef = useRef(null)
  const reportTypeRef = useRef(null)
  const tempReportTypeRef = useRef(null)
  const runSnackBar = useContext(SnackBarContext)

  const withTimestamp = reportType === CONVERSION_REPORT_TYPE.WITH_TIMESTAMP
  const {
    hideModal,
    handleRefresh,
    startRunningValidation,
    startRunningDeleting,
    handleConversionsUploadSuccess,
    handleConversionsUploadError,
  } = useConversionsUpload(withTimestamp)

  const { data, isLoaded } = useGraphQLFetch({
    fetchFn: getConversionReportType,
    defaultFetchArgs: campaignId,
    fetchId: 'getConversionReportType',
    mapResponse: data => data.campaign,
  })

  useEffect(() => {
    if (isLoaded && data) {
      const metricsInfo = (data.metrics ? data.metrics.nodes || [] : []).sort(
        (a, b) => (b.isPrimary ? 1 : 0),
      )
      setReportType(data.conversionReportType || CONVERSION_REPORT_TYPE.NO_TYPE)
      setCampaignName(data.name || 'campaign')
      setCampaignType(data.subtype || data.type || null)
      setMetrics(
        metricsInfo.map(metric => (metric.label ? metric.label : metric.name)),
      )
      setPrimaryMetric(
        metricsInfo.reduce((acc, metric) => {
          if (metric.isPrimary) acc = metric.name
          return acc
        }, ''),
      )
      setOtherMetrics(
        metricsInfo
          .filter(metric => !metric.isPrimary)
          .map(metric => metric.name),
      )
      setCountryCode(data.country.code || '')
      setConversionsCount(data.conversionsCount || 0)
      setPaidConversionsCount(data.paidConversionsCount || 0)
    }
  }, [isLoaded, data])

  const updateTempReportType = () => {
    if (
      reportTypeRef.current &&
      reportTypeRef.current.reportType !== reportType
    ) {
      tempReportTypeRef.current = reportTypeRef.current.reportType
      handleChangeReportType()
    } else {
      hideModal()
    }
  }

  const updateReportType = async () => {
    if (tempReportTypeRef.current && tempReportTypeRef.current !== reportType) {
      const response = await updateConversionReportType({
        campaignId,
        conversionReportType: tempReportTypeRef.current,
      })
      const result = await response.json()
      const newReportType =
        result.data.updateCampaign.campaign.conversionReportType || reportType
      if (newReportType !== reportType) {
        runSnackBar({
          type: 'success',
          vertical: 'top',
          msg: 'Report type was changed.',
        })
        setReportType(newReportType)
      }
    }
    hideModal()
  }

  const handleChangeReportType = () => {
    dispatch(
      showReduxModal({
        title: 'Change Report Type',
        children: (
          <ChangeReportType onConfirm={updateReportType} onCancel={hideModal} />
        ),
        isReportModal: true,
        hideButtons: true,
        aspect: 'conversions',
      }),
    )
  }

  const handleClickReportType = () => {
    dispatch(
      showReduxModal({
        title: 'Report type settings',
        onConfirm: updateTempReportType,
        children: (
          <SelectReportType
            type={reportType}
            existConversions={conversionsCount > 0}
            ref={reportTypeRef}
          />
        ),
        isReportModal: true,
        hideModalOnConfirm: false,
        hideCancel: true,
        confirmLabel: 'Save',
        aspect: 'conversions',
      }),
    )
  }

  const warnWithoutReportType = () => {
    if (reportType === CONVERSION_REPORT_TYPE.NO_TYPE) {
      runSnackBar({
        type: 'error',
        vertical: 'top',
        msg: 'Please select the report type first',
      })
      return true
    }
    return false
  }

  const handleChangeReportFile = async ([report]) => {
    if (warnWithoutReportType()) return
    setReportFile(report)
  }

  const handleUploadReport = () => {
    if (warnWithoutReportType()) return
    if (dropzoneRef.current) {
      dropzoneRef.current.open()
    }
  }

  const handleRemoveReport = () => {
    setReportFile(null)
    if (dropzoneRef.current) {
      dropzoneRef.current.props.onDrop([])
    }
  }

  const handleSuccess = () => {
    handleConversionsUploadSuccess()
    handleRemoveReport()
  }

  const handleError = (
    errorType,
    handleConfirm,
    handleCancel,
    data,
    withTimestamp,
  ) => {
    handleConversionsUploadError(
      errorType,
      handleConfirm,
      handleCancel,
      data,
      withTimestamp,
    )
    handleRemoveReport()
  }

  const handleSubmitReport = () => {
    startRunningValidation()

    const callbacks = {
      onSuccess: () => {
        handleSuccess()
      },
      onError: (
        errorType,
        handleConfirm,
        handleCancel,
        data = null,
        withTimestamp = 0,
      ) => {
        setTimeout(() => {
          handleError(
            errorType,
            handleConfirm,
            handleCancel,
            data,
            withTimestamp,
          )
        }, 1000)
      },
      stopLoading: () => {
        hideModal()
      },
    }
    validateRule({
      stage: 0,
      campaignId,
      campaignType,
      reportFile,
      callbacks,
      withTimestamp,
      metrics,
      conversionsCount,
      isUSFormat: countryCode === 'US',
      startRunningValidation,
    })
  }

  const handleSuccessDeleteConversions = () => {
    hideModal()
    runSnackBar({
      type: 'success',
      vertical: 'top',
      msg: 'Conversions deleted!',
    })
    setTimeout(() => {
      handleRefresh()
    }, 2000)
  }

  const handleConfirmDeleteConversions = async type => {
    startRunningDeleting()

    const includePaid = type !== 2
    const includeUnPaid = type !== 1
    const response = await deleteConversions({
      campaignId,
      includePaid,
      includeUnPaid,
    })
    const { count } = (await response.json()).data.deleteConversions
    if (count) {
      handleSuccessDeleteConversions()
    }
  }

  const handleClickDeleteConversions = () => {
    if (!withTimestamp) {
      dispatch(
        showReduxModal({
          title: 'Delete Conversions',
          children: (
            <DeleteConversions
              totalCount={conversionsCount}
              paidCount={paidConversionsCount}
              primaryMetric={primaryMetric}
              otherMetrics={otherMetrics}
              onConfirm={handleConfirmDeleteConversions}
              onCancel={hideModal}
            />
          ),
          isReportModal: true,
          hideButtons: true,
          aspect: 'conversions',
        }),
      )
    } else {
      dispatch(
        showReduxModal({
          title: 'Not allowed',
          children: <NotAllowed />,
          isReportModal: true,
          hideCancel: true,
          confirmLabel: 'Understood',
          aspect: 'conversions',
        }),
      )
    }
  }

  const renderDropzoneLabel = ({ metrics, reportType }) => {
    if (!metrics || metrics.length == 0) {
      return 'Please add some metrics before conversions upload'
    }
    if (reportType !== CONVERSION_REPORT_TYPE.NO_TYPE) {
      return 'Drop file here'
    } else {
      return 'Select report type'
    }
  }

  if (!countryCode) {
    return null
  }

  return (
    <CampaignPage
      active={nav}
      history={history}
      match={match}
      campaignId={campaignId}
    >
      <ConversionsView
        reportType={reportType}
        campaignName={campaignName}
        metrics={metrics}
        reportFile={reportFile}
        onClickReportType={handleClickReportType}
        onUploadReport={handleUploadReport}
        onRemoveReport={handleRemoveReport}
        onSubmitReport={handleSubmitReport}
        fileUploader={
          <Dropzone
            innerRef={dropzoneRef}
            isReport
            reportTypeSelected={reportType !== CONVERSION_REPORT_TYPE.NO_TYPE}
            disableClick
            disabled={!metrics || metrics.length == 0}
            dropzoneLabel={renderDropzoneLabel({ metrics, reportType })}
            input={{ onChange: handleChangeReportFile }}
            accept={'text/csv'}
          />
        }
      >
        <ConversionStatistics campaignId={campaignId} />
        <ConversionList
          isUSFormat={countryCode === 'US'}
          campaignId={campaignId}
          handleSuccessDeleteConversions={handleSuccessDeleteConversions}
          hideModal={hideModal}
          startRunningDeleting={startRunningDeleting}
        />
        {conversionsCount > 0 && (
          <StyledDeleteConversionsButton onClick={handleClickDeleteConversions}>
            Delete Conversions
          </StyledDeleteConversionsButton>
        )}
      </ConversionsView>
    </CampaignPage>
  )
}

export default Conversions
